import { useCallback, useEffect, useRef, useState } from "react";

/** Tracks which keyed elements intersect the viewport, including a small margin. */
export function useInViewSet(): {
  visible: ReadonlySet<string>;
  observe: (key: string) => (node: HTMLElement | null) => void;
} {
  const [visible, setVisible] = useState<ReadonlySet<string>>(() => new Set());
  const nodes = useRef(new Map<string, HTMLElement>());
  const observer = useRef<IntersectionObserver | null>(null);
  const callbacks = useRef(new Map<string, (node: HTMLElement | null) => void>());

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        setVisible((prev) => {
          const next = new Set(prev);
          let changed = false;
          for (const entry of entries) {
            const key = (entry.target as HTMLElement).dataset.viewKey;
            if (!key) continue;
            if (entry.isIntersecting && !next.has(key)) {
              next.add(key);
              changed = true;
            } else if (!entry.isIntersecting && next.has(key)) {
              next.delete(key);
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      },
      { root: null, rootMargin: "120px", threshold: 0.15 },
    );
    observer.current = obs;
    for (const node of nodes.current.values()) obs.observe(node);
    return () => {
      obs.disconnect();
      observer.current = null;
    };
  }, []);

  const observe = useCallback((key: string) => {
    const existing = callbacks.current.get(key);
    if (existing) return existing;
    const cb = (node: HTMLElement | null) => {
      const prev = nodes.current.get(key);
      if (prev && prev !== node) observer.current?.unobserve(prev);
      if (!node) {
        nodes.current.delete(key);
        setVisible((current) => {
          if (!current.has(key)) return current;
          const next = new Set(current);
          next.delete(key);
          return next;
        });
        return;
      }
      node.dataset.viewKey = key;
      nodes.current.set(key, node);
      observer.current?.observe(node);
    };
    callbacks.current.set(key, cb);
    return cb;
  }, []);

  return { visible, observe };
}
